import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeProvider, css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import Breakpoints, { maximumWidth, } from '@polestar/component-warehouse-react/tokens/breakpoints/Breakpoints';
import Alignments, { MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Grid from '@polestar/component-warehouse-react/components/grid/Grid';
import GridColumn from '@polestar/component-warehouse-react/components/grid/GridColumn';
import Image from '@polestar/component-warehouse-react/components/image/Image';
import VideoPlayer from '@polestar/component-warehouse-react/components/video-player/VideoPlayer';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import { MotionDurations, MotionEasings, createCSSTransition, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import PauseButton from '@polestar/component-warehouse-react/components/pause-button/PauseButton';
import Themes from '@polestar/component-warehouse-react/themes/Themes';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { HeroSectionVisualContentPlacement, HeroSectionVisualMediaVariant, HeroSectionVisualTheme, } from '../HeroSection';
import { HeroSectionCommonButtonGroup, HeroSectionCommonTitleGroup, contentGridCols, } from './HeroSectionCommon';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
const mediaIdealHeightMap = {
    [Sizes.small]: '40vh',
    [Sizes.medium]: '60vh',
    [Sizes.large]: '80vh',
};
const mediaMinHeightMap = {
    [DeviceTypes.mobile]: {
        [Sizes.small]: createCustomSpacing(267 / 16),
        [Sizes.medium]: createCustomSpacing(401 / 16),
        [Sizes.large]: createCustomSpacing(534 / 16),
    },
    [DeviceTypes.tablet]: {
        [Sizes.small]: createCustomSpacing(410 / 16),
        [Sizes.medium]: createCustomSpacing(615 / 16),
        [Sizes.large]: createCustomSpacing(820 / 16),
    },
    [DeviceTypes.desktop]: {
        [Sizes.small]: createCustomSpacing(288 / 16),
        [Sizes.medium]: createCustomSpacing(432 / 16),
        [Sizes.large]: createCustomSpacing(576 / 16),
    },
    [DeviceTypes.desktopWide]: {
        [Sizes.small]: createCustomSpacing(320 / 16),
        [Sizes.medium]: createCustomSpacing(480 / 16),
        [Sizes.large]: createCustomSpacing(640 / 16),
    },
};
const getMediaHeight = (mediaHeight, deviceType) => {
    if (!mediaHeight) {
        return undefined;
    }
    const size = mediaHeight[deviceType];
    return size
        ? {
            minHeight: mediaMinHeightMap[deviceType][size],
            idealHeight: mediaIdealHeightMap[size],
        }
        : undefined;
};
const HeroSectionVisual = forwardRef(({ title, tagline, intro, buttonOne, buttonTwo, layoutBleed = false, mediaHeight, image, video, videoRef, mediaVariant, overlayAlignment = Alignments.center, buttonPosition, sectionTheme = HeroSectionVisualTheme.light, titlePosition, ...props }, ref) => {
    var _a, _b;
    // Warnings for incompatible prop combinations
    if (!intro &&
        (buttonPosition === HeroSectionVisualContentPlacement.below ||
            titlePosition === HeroSectionVisualContentPlacement.below)) {
        warnOnce('HeroSectionVisual', 'You have set buttonPosition or titlePosition to "below" but have not provided an intro. Please provide an intro or change the buttonPosition and/or titlePosition to "overlay".');
    }
    if (!intro && buttonPosition === HeroSectionVisualContentPlacement.overlay) {
        warnOnce('HeroSectionVisual', 'You have set buttonPosition to "overlay" but have not provided an intro. Please provide an intro or change the buttonPosition to "below".');
    }
    if (mediaVariant === HeroSectionVisualMediaVariant.video &&
        (buttonPosition === HeroSectionVisualContentPlacement.overlay ||
            titlePosition === HeroSectionVisualContentPlacement.overlay)) {
        warnOnce('HeroSectionVisual', 'You have set buttonPosition or titlePosition to "overlay" but have set mediaVariant to "video". Please set mediaVariant to "image" or "videoBackground", or change the buttonPosition and/or titlePosition to "below".');
    }
    if (((mediaVariant === HeroSectionVisualMediaVariant.videoBackground ||
        mediaVariant === HeroSectionVisualMediaVariant.image) &&
        titlePosition === HeroSectionVisualContentPlacement.below &&
        buttonPosition === HeroSectionVisualContentPlacement.overlay) ||
        (titlePosition === HeroSectionVisualContentPlacement.overlay &&
            buttonPosition === HeroSectionVisualContentPlacement.below)) {
        warnOnce('HeroSectionVisual', 'You have set buttonPosition or titlePosition to "overlay", however the title or buttons are below. Please set buttonPosition or titlePosition to "below" or change the titlePosition or buttonPosition to "overlay".');
    }
    const overlayAlignmentMap = {
        [Alignments.top]: MainAxisAlignments.start,
        [Alignments.center]: MainAxisAlignments.center,
        [Alignments.bottom]: MainAxisAlignments.end,
    };
    const [isMobile, setIsMobile] = useState(false);
    const handleResize = () => {
        setIsMobile(window.innerWidth < Breakpoints.tablet);
    };
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const internalVideoRef = useRef();
    const combinedVideoRef = useCombinedRef(videoRef, internalVideoRef);
    /**
     * Assign HeroSectionCommonTitleGroup and
     * HeroSectionCommonButtonGroup to local Components
     */
    const TitleGroup = () => React.createElement(HeroSectionCommonTitleGroup, { title: title, tagline: tagline });
    const ButtonGroup = () => (React.createElement(HeroSectionCommonButtonGroup, { buttonOne: buttonOne, buttonTwo: buttonTwo }));
    return (React.createElement(SectionVisual, { ...props, ref: ref },
        React.createElement(ThemeProvider, { theme: sectionTheme === HeroSectionVisualTheme.light ? Themes.light : Themes.dark },
            React.createElement(GridSection, { isGrid: !layoutBleed },
                mediaVariant !== HeroSectionVisualMediaVariant.video && (React.createElement(React.Fragment, null,
                    React.createElement(OverlayContent, { mainAxisAlignment: overlayAlignmentMap[overlayAlignment] },
                        React.createElement(Grid, null,
                            React.createElement(GridColumn, { span: contentGridCols(layoutBleed) },
                                React.createElement(OverlayContentContainer, null,
                                    titlePosition === HeroSectionVisualContentPlacement.overlay && (React.createElement(TitleGroup, null)),
                                    intro && buttonPosition === HeroSectionVisualContentPlacement.overlay && (React.createElement(ButtonGroup, null)))))),
                    mediaVariant === HeroSectionVisualMediaVariant.videoBackground && (
                    // We needed to place this here at the same level of the OverlayContent due to z-index interaction issues
                    React.createElement(StyledPauseButton, { mediaRef: videoRef || internalVideoRef, ariaLabels: (video === null || video === void 0 ? void 0 : video.ariaLabels)
                            ? {
                                play: (_a = video.ariaLabels) === null || _a === void 0 ? void 0 : _a.play,
                                pause: (_b = video.ariaLabels) === null || _b === void 0 ? void 0 : _b.pause,
                            }
                            : {
                                play: 'Resume details video',
                                pause: 'Pause details video',
                            } })))),
                React.createElement(StyledMediaContainer, { mediaHeight: mediaHeight },
                    mediaVariant === HeroSectionVisualMediaVariant.image && React.createElement(StyledImage, { ...image }),
                    mediaVariant === HeroSectionVisualMediaVariant.video && (React.createElement(VideoPlayerWithControls, { video: video, videoRef: videoRef })),
                    mediaVariant === HeroSectionVisualMediaVariant.videoBackground && (React.createElement(VideoWithPauseButton, { video: video, videoRef: videoRef || internalVideoRef, combinedVideoRef: combinedVideoRef }))))),
        React.createElement(GridSection, { isGrid: true, isCollapse: true },
            React.createElement(StyledGridBelow, null,
                React.createElement(GridColumn, { span: { mobile: 4, tablet: 8, desktop: 4 } }, titlePosition === HeroSectionVisualContentPlacement.below && React.createElement(TitleGroup, null)),
                React.createElement(GridColumn, { span: { mobile: 4, tablet: 8, desktop: 8 } }, intro && (React.createElement(BelowContent, { hasGap: titlePosition === HeroSectionVisualContentPlacement.below ||
                        buttonPosition === HeroSectionVisualContentPlacement.below },
                    React.createElement(Paragraph, { size: isMobile ? Sizes.medium : Sizes.large }, intro),
                    buttonPosition === HeroSectionVisualContentPlacement.below && React.createElement(ButtonGroup, null))))))));
});
HeroSectionVisual.displayName = 'HeroSectionVisual';
export default HeroSectionVisual;
// videoRef || internalVideoRef
const VideoWithPauseButton = forwardRef(({ video, combinedVideoRef }, ref) => {
    var _a;
    return (React.createElement("div", { ref: ref, "data-type": "video" },
        React.createElement(StyledNativeVideo, { tabIndex: -1, ref: combinedVideoRef, ...video, loop: true, muted: true, width: "100%", height: "100%" }, (_a = video.sources) === null || _a === void 0 ? void 0 : _a.map((source, sourceIndex) => (React.createElement("source", { key: `source${sourceIndex}`, ...source }))))));
});
const VideoPlayerWithControls = forwardRef(({ videoRef, video }, ref) => {
    var _a;
    return (React.createElement(VideoPlayer, { ...video, ref: videoRef || ref, playsInline: true, containerProps: {
            as: motion.div,
            'data-type': 'video',
            style: {
                pointerEvents: 'auto',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
            },
        }, 
        /**
         * We need to override the default implementation of the VideoPlayer for this to work in our HeroSection
         */
        style: {
            position: 'absolute',
            objectFit: 'cover',
        } }, (_a = video.sources) === null || _a === void 0 ? void 0 : _a.map((source, sourceIndex) => (React.createElement("source", { key: `source${sourceIndex}`, ...source })))));
});
const SectionVisual = styled('section', shouldNotForwardProps(true, 'type'))(css `
  position: relative;
  display: flex;
  flex-direction: column;
  transition: ${createCSSTransition('padding', MotionEasings.easeOut, MotionDurations.fast)};
  ${responsiveStyles({
    paddingInline: {
        mobile: Grids.mobile.gutter,
        tablet: Grids.tablet.gutter,
        desktop: Grids.desktop.gutter,
    },
    paddingBlockEnd: {
        mobile: Spacings.large,
        tablet: Spacings.xLarge,
        desktop: createCustomSpacing(2.5),
    },
    gap: {
        mobile: Spacings.large,
        tablet: Spacings.xLarge,
        desktop: createCustomSpacing(40 / 16),
    },
    width: {
        mobile: 'auto',
        tablet: '100%',
    },
})}
`);
const OverlayContent = styled(Column)(css `
  position: relative;
  grid-area: content;
  gap: ${Spacings.large};
  height: 100%;
  z-index: 2;
`);
const OverlayContentContainer = styled('div')(css `
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.large};
`);
const BelowContent = styled(Column, shouldNotForwardProps(true, 'hasGap'))(({ hasGap }) => {
    return css(css `
      gap: ${Spacings.none};
    `, hasGap &&
        css `
        ${responsiveStyles({
            gap: {
                mobile: Spacings.medium,
                tablet: Spacings.large,
            },
        })}
      `);
});
const StyledMediaContainer = styled('div', shouldNotForwardProps(true, 'mediaHeight'))(({ mediaHeight }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return css(css `
    --min-height: ${((_a = getMediaHeight(mediaHeight, DeviceTypes.mobile)) === null || _a === void 0 ? void 0 : _a.minHeight) ||
        mediaMinHeightMap[DeviceTypes.mobile][Sizes.medium]};
    --ideal-height: ${(_b = getMediaHeight(mediaHeight, DeviceTypes.mobile)) === null || _b === void 0 ? void 0 : _b.idealHeight};

    position: relative;
    display: flex;
    grid-area: media;
    min-height: var(--min-height, ${mediaIdealHeightMap[Sizes.medium]});
    height: var(--ideal-height, ${mediaIdealHeightMap[Sizes.medium]});
    z-index: 1;
    transition: ${createCSSTransition('min-height', MotionEasings.easeOut, MotionDurations.fast)};

    /**
       * Using this method allows us to avoid using the useMedia hook
       * and therefore the problem of re-renders associated with it.
       */
    ${MediaQueries.tablet} {
      --min-height: ${((_c = getMediaHeight(mediaHeight, DeviceTypes.tablet)) === null || _c === void 0 ? void 0 : _c.minHeight) ||
        mediaMinHeightMap[DeviceTypes.tablet][Sizes.medium]};
      --ideal-height: ${(_d = getMediaHeight(mediaHeight, DeviceTypes.tablet)) === null || _d === void 0 ? void 0 : _d.idealHeight};
    }

    ${MediaQueries.desktop} {
      --min-height: ${((_e = getMediaHeight(mediaHeight, DeviceTypes.desktop)) === null || _e === void 0 ? void 0 : _e.minHeight) ||
        mediaMinHeightMap[DeviceTypes.desktop][Sizes.medium]};
      --ideal-height: ${(_f = getMediaHeight(mediaHeight, DeviceTypes.desktop)) === null || _f === void 0 ? void 0 : _f.idealHeight};
    }

    ${MediaQueries.desktopWide} {
      --min-height: ${((_g = getMediaHeight(mediaHeight, DeviceTypes.desktopWide)) === null || _g === void 0 ? void 0 : _g.minHeight) ||
        mediaMinHeightMap[DeviceTypes.desktopWide][Sizes.medium]};
      --ideal-height: ${(_h = getMediaHeight(mediaHeight, DeviceTypes.desktopWide)) === null || _h === void 0 ? void 0 : _h.idealHeight};
    }
  `);
});
const StyledGridBelow = styled(Grid)(css `
  grid-column: content;
  grid-row: content;
  ${responsiveStyles({
    rowGap: {
        mobile: Spacings.medium,
        tablet: Spacings.medium,
    },
})}
`);
const GridSection = styled('section', shouldNotForwardProps(true, 'isGrid', 'isCollapse'))(({ isGrid, isCollapse }) => {
    return css(css `
      ${responsiveStyles({
        '--grid-column-gutter': {
            mobile: Grids.mobile.gutter,
            tablet: Spacings.large,
            desktop: Spacings.xLarge,
            desktopWide: Spacings.xxxxLarge,
        },
        '--grid-row-gutter': {
            mobile: Spacings.xLarge,
            tablet: Spacings.xLarge,
            desktop: Spacings.xxLarge,
            desktopWide: Spacings.xxxxLarge,
        },
        maxWidth: {
            mobile: '100%',
            desktopWide: `${maximumWidth}px`,
        },
    })};
      position: relative;
      margin: 0 auto;
      display: grid;
      width: 100%;
      transition: ${createCSSTransition('all', MotionEasings.easeOut, MotionDurations.fast)};

      grid-template-columns:
        [grid-start media-start] var(--grid-column-gutter)
        [content-start] 1fr
        [content-end] var(--grid-column-gutter)
        [media-end grid-end];

      grid-template-rows:
        [grid-start media-start] var(--grid-row-gutter)
        [content-start] 1fr
        [content-end] var(--grid-row-gutter)
        [grid-end media-start];
    `, isGrid &&
        css `
        ${responsiveStyles({
            '--grid-column-gutter': {
                mobile: Spacings.small,
                tablet: Spacings.large,
                desktop: Spacings.xLarge,
                desktopWide: Spacings.xxxxLarge,
            },
            paddingInline: {
                mobile: Grids.mobile.gutter,
                tablet: Spacings.large,
                desktop: Spacings.xLarge,
                desktopWide: Spacings.xxxxLarge,
            },
        })};
      `, isCollapse &&
        css `
        ${responsiveStyles({
            '--grid-row-gutter': {
                mobile: Spacings.none,
                tablet: Spacings.none,
                desktop: Spacings.none,
                desktopWide: Spacings.none,
            },
            '--grid-column-gutter': {
                mobile: Spacings.none,
                tablet: Spacings.none,
                desktop: Spacings.none,
                desktopWide: Spacings.none,
            },
        })};
      `);
});
const StyledImage = styled(Image)(css `
  position: absolute;
`);
const StyledNativeVideo = styled('video')(css `
  position: absolute;
  object-fit: cover;
`);
const StyledPauseButton = styled(PauseButton)(css `
  position: absolute;
  grid-area: grid;
  inset-block-end: ${Spacings.large};
  inset-inline-end: ${Spacings.large};
  z-index: 2;
`);
